import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { UseNodeLogic_LimitedAuthorizationsFragment$key } from "@generated/UseNodeLogic_LimitedAuthorizationsFragment.graphql";
import {
	ElementTypeV2,
	UseNodeLogic_NodeFragment$key,
} from "@generated/UseNodeLogic_NodeFragment.graphql";
import { ModuleRatingSummaryComponent } from "../../app/components/left-frame/parts/module-rating-summary";
import { NodeCardBadgeComponent } from "../../app/components/nodecard/NodeCardBadgeComponent";
import { NodeCardCertificateButton } from "../../app/components/nodecard/NodeCardCertificateButton";
import {
	NodeCardDateComponent,
	TextContainer,
} from "../../app/components/nodecard/NodeCardDate.component";
import { NodeCardDurationAndAwardComponent } from "../../app/components/nodecard/NodeCardDurationAndAward.component";
import { NodeCardProgressBarComponent } from "../../app/components/nodecard/NodeCardProgressBar.component";
import { NodeCardProgressBarTextComponent } from "../../app/components/nodecard/NodeCardProgressBarText.component";
import { NodeCardTitleComponent } from "../../app/components/nodecard/NodeCardTitle.component";
import { NodeIconComponent } from "../../app/components/nodecard/NodeIcon.component";
import { CalendarIcon } from "../../app/components/nodecard/SvgNodeIcons.component";
import { useCardAdvanceTreeNavigation } from "../../app/components/nodecard/useCardAdvanceTreeNavigation";
import { NodeCardContainer } from "../../app/containers/NodeCard.container";
import { NodeCardContentContainer } from "../../app/containers/NodeCardContent.container";
import { NodeCardFooterContainer } from "../../app/containers/NodeCardFooterContainer";
import { NodeCardHeaderContainer } from "../../app/containers/NodeCardHeader.container";
import { NodeCardIconTextContainer } from "../../app/containers/NodeCardIconText.container";
import { NodeCardInformationContainer } from "../../app/containers/NodeCardInformation.container";
import { useNodeLogic } from "../../app/utils/UseNodeLogic";
import { formatDateTime } from "../../infecto-lms-webapp/components/DateTimeDisplay.component";
import { getLearnOpportunityPath } from "../router/routes/auth/nodes.routes";

interface OwnProps {
	learnOpportunityFragmentRef?: UseNodeLogic_NodeFragment$key;

	nodeWithoutUserLearnStateFragmentRef?: UseNodeLogic_LimitedAuthorizationsFragment$key;
	ignoreUserLearnState: boolean;
}

export const MultipleChoiceElements: ElementTypeV2 = "multipleChoice";

export const NodeCard = ({
	learnOpportunityFragmentRef,
	nodeWithoutUserLearnStateFragmentRef,
	ignoreUserLearnState,
}: OwnProps) => {
	const nodeLogic = useNodeLogic({
		learnOpportunityFragmentRef,
		nodeWithoutUserLearnStateFragmentRef,
		ignoreUserLearnState,
	});

	return nodeLogic.isLoggedIn ? (
		<AuthenticatedNodeCard nodeLogic={nodeLogic} ignoreUserLearnState={ignoreUserLearnState} />
	) : (
		<AnonymousNodeCard nodeLogic={nodeLogic} ignoreUserLearnState={ignoreUserLearnState} />
	);
};

type NodeCardLogic = ReturnType<typeof useNodeLogic>;

interface NodeCardWithNavigationProps {
	nodeLogic: NodeCardLogic;
	ignoreUserLearnState: boolean;
}

const AuthenticatedNodeCard = ({
	nodeLogic,
	ignoreUserLearnState,
}: NodeCardWithNavigationProps) => {
	const treeNavigationHandler = useCardAdvanceTreeNavigation({
		contentNodeId: nodeLogic.targetContentNodeId,
	});

	const handleNavigation = useCallback(() => {
		treeNavigationHandler();
	}, [treeNavigationHandler]);

	return (
		<NodeCardView
			nodeLogic={nodeLogic}
			ignoreUserLearnState={ignoreUserLearnState}
			handleNavigation={handleNavigation}
		/>
	);
};

const AnonymousNodeCard = ({ nodeLogic, ignoreUserLearnState }: NodeCardWithNavigationProps) => {
	const navigate = useNavigate();

	const handleNavigation = useCallback(() => {
		localStorage.setItem("landingPage", "true");
		localStorage.setItem("learnOpportunityId", nodeLogic.learnOpportunity.id);
		navigate(getLearnOpportunityPath(nodeLogic.learnOpportunity.id));
	}, [navigate, nodeLogic.learnOpportunity.id]);

	return (
		<NodeCardView
			nodeLogic={nodeLogic}
			ignoreUserLearnState={ignoreUserLearnState}
			handleNavigation={handleNavigation}
		/>
	);
};

interface NodeCardViewProps extends NodeCardWithNavigationProps {
	handleNavigation: () => void;
}

const NodeCardView = ({ nodeLogic, ignoreUserLearnState, handleNavigation }: NodeCardViewProps) => {
	const {
		learnOpportunity,
		multipleChoiceModul,
		isShowingBadge,
		hasNoLekButActiveContentSubmission,
		progressBarState,
		componentColor,
		numIssuedCertificates,
		doesNodeExpiresBeforeUserGetsUnblocked,
		hasCanNotBeRestarteAfterPassedConfig,
	} = nodeLogic;

	const internalCode = learnOpportunity.structureDefinition.extension?.internalCode;

	return (
		<NodeCardContainer
			onClick={() => handleNavigation}
			isVisibleOnlyForAdmins={
				learnOpportunity.structureDefinition.extension?.isVisibleOnlyForAdmins as boolean
			}
		>
			<NodeCardContentContainer>
				<div className="h-full w-full" onClick={handleNavigation}>
					<NodeCardHeaderContainer showLabelCard={isShowingBadge}>
						{isShowingBadge ? (
							<NodeCardBadgeComponent
								ignoreUserLearnState={ignoreUserLearnState}
								learnOpportunityFragmentRef={learnOpportunity}
								color={componentColor}
								isNodeFinished={
									numIssuedCertificates > 0 ||
									hasNoLekButActiveContentSubmission ||
									hasCanNotBeRestarteAfterPassedConfig
								}
							/>
						) : null}
						<NodeIconComponent learnOpportunityFragmentRef={learnOpportunity} />
					</NodeCardHeaderContainer>

					<NodeCardInformationContainer>
						<NodeCardTitleComponent title={learnOpportunity.structureDefinition.title} />

						{ignoreUserLearnState ? (
							<ElementsWrapper>
								<NodeCardIconTextContainer>
									<CalendarIcon></CalendarIcon>
									<TextContainer>
										{multipleChoiceModul &&
										learnOpportunity.structureDefinition.extension?.configConsequences
											?.isRewardedTo &&
										!learnOpportunity.structureDefinition.extension?.configConsequences
											?.rewardExpired
											? `bis ${formatDateTime(
													learnOpportunity.structureDefinition.extension?.configConsequences
														.isRewardedTo,
													false,
												)} zertifiziert`
											: `verfügbar seit ${formatDateTime(
													//eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
													learnOpportunity.structureDefinition.extension?.firstReleasedAt!,

													false,
												)}`}
									</TextContainer>
								</NodeCardIconTextContainer>
							</ElementsWrapper>
						) : (
							<ElementsWrapper>
								<NodeCardDateComponent
									doesNodeExpiresBeforeUserGetsUnblocked={doesNodeExpiresBeforeUserGetsUnblocked}
									isUserRewardedWithCertificate={numIssuedCertificates > 0}
									learnOpportunityFragmentRef={learnOpportunity}
									hasCanNotBeRestarteAfterPassed={hasCanNotBeRestarteAfterPassedConfig}
									hasNoLekButActiveContentSubmission={hasNoLekButActiveContentSubmission}
								></NodeCardDateComponent>
							</ElementsWrapper>
						)}
						<ElementsWrapper>
							<NodeCardDurationAndAwardComponent
								isUserRewardedWithCertificate={numIssuedCertificates > 0}
								learnOpportunityFragmentRef={learnOpportunity}
							/>
						</ElementsWrapper>
						{!ignoreUserLearnState && (
							<ElementsWrapper>
								<div className="flex w-full align-items-center">
									<ModuleRatingSummaryComponent
										isAlwaysVisible={false}
										nodeRef={learnOpportunity.id}
									/>
								</div>
							</ElementsWrapper>
						)}
					</NodeCardInformationContainer>
				</div>

				{!ignoreUserLearnState && numIssuedCertificates > 0 ? (
					<NodeCardCertificateButton learnOpportunityFragmentRef={learnOpportunity} />
				) : (
					<div className="w-full align-items-start">
						<div
							className="h-full"
							onClick={() => {
								handleNavigation();
							}}
						>
							{ignoreUserLearnState ? (
								<div className="flex-column w-full">
									<NodeCardProgressBarTextComponent isBold={false}>
										{multipleChoiceModul ? "Lernfortschritt" : "Ohne Lernerfolgskontrolle"}
									</NodeCardProgressBarTextComponent>

									<NodeCardProgressBarComponent
										progressBarState={progressBarState}
										componentColor={componentColor}
										isBlocked={false}
										percentage={0}
									/>
								</div>
							) : (
								<NodeCardFooterContainer
									progressBarState={progressBarState}
									componentColor={componentColor}
									learnOpportunityFragmentRef={learnOpportunity}
								></NodeCardFooterContainer>
							)}
						</div>
					</div>
				)}
				<div className="w-full align-items-end h-1rem">
					{internalCode && <ModuleNumberWrapper>Modulnummer: {internalCode}</ModuleNumberWrapper>}
				</div>
			</NodeCardContentContainer>
		</NodeCardContainer>
	);
};

const ModuleNumberWrapper = styled.div`
	float: right;
	color: #787878;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
`;

const ElementsWrapper = styled.div`
	margin-top: 5px;
	margin-bottom: 5px;
`;
